import { Tooltip } from 'components/Basic/Tooltip/Tooltip';
import { TClubLogo } from 'components/Blocks/LoyaltyClub/TClubLogo';
import {
    HighButtonStyled,
    HighLinkStyled,
    LoyaltyClubBenefitsLinkStyled,
    LoyaltyClubBenefitsStyled,
    LoyaltyClubBenefitStyled,
    LoyaltyClubHeadingStyled,
    LoyaltyClubLeftColumnStyled,
    LoyaltyClubPointsInfoStyled,
    LoyaltyClubRightColumnStyled,
    LoyaltyClubSigningButtonWrapperStyled,
    LoyaltyClubStyled,
    SignUpWrapperStyled,
} from 'components/Pages/Cart/LoyaltyClub/LoyaltyClub.style';
import { LoyaltyClubUsePointsForm } from 'components/Pages/Cart/LoyaltyClub/LoyaltyClubUsePointsForm';
import { useCurrentCart } from 'connectors/cart/Cart';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import { SimpleCheckmarkIcon } from 'public/svg/IconsSvg';
import { useEffect, useState } from 'react';
import { LoyaltyClubPointsType } from 'types/loyaltyClub';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';

type LoyaltyClubContentProps = {
    loyaltyClubInfo: LoyaltyClubPointsType | null;
    hasVoucherProduct: boolean;
    setIsUserPopupOpened: CallableFunction;
};

const LoyaltyClubContent: FC<LoyaltyClubContentProps> = ({
    loyaltyClubInfo,
    hasVoucherProduct,
    setIsUserPopupOpened,
}) => {
    const testIdentifier = 'blocks-loyaltyclub';

    const { isUserLoggedIn } = useCurrentUserData();
    const { loyaltyClubPointsMaximum } = useCurrentCart();
    const maximumApplicableLoyaltyPoints = Math.max(loyaltyClubPointsMaximum ?? 0, 0);
    const [pointsToBeApplied, setPointsToBeApplied] = useState<number>(
        Math.max(loyaltyClubInfo?.loyaltyClubPoints ?? 0, 0),
    );
    const t = useTypedTranslationFunction();
    const { url: domainUrl } = useDomainConfig();
    const [registrationUrl, tescomaClubUrl] = getInternationalizedStaticUrls(
        ['/registration', '/tescoma-club'],
        domainUrl,
    );
    const loyaltyClubBlockId = 'loyalty-club';
    const [isZeroPoints, setIsZeroPoints] = useState<boolean>(pointsToBeApplied <= 0);
    const disableControlsBecauseVoucher = hasVoucherProduct && !loyaltyClubInfo?.voucherUseAllowed;

    useEffect(() => {
        let newLoyaltyClubPointsToBeApplied = Math.min(
            maximumApplicableLoyaltyPoints,
            loyaltyClubInfo?.loyaltyClubPoints ?? maximumApplicableLoyaltyPoints,
        );

        newLoyaltyClubPointsToBeApplied = Math.max(newLoyaltyClubPointsToBeApplied, 0);

        setPointsToBeApplied(newLoyaltyClubPointsToBeApplied);
    }, [maximumApplicableLoyaltyPoints, loyaltyClubInfo?.loyaltyClubPoints]);

    return (
        <LoyaltyClubStyled data-testid={testIdentifier} id={loyaltyClubBlockId}>
            <LoyaltyClubLeftColumnStyled>
                <TClubLogo />
                {isUserLoggedIn ? (
                    loyaltyClubInfo && (
                        <LoyaltyClubHeadingStyled type="h3">{t('Use your points')}</LoyaltyClubHeadingStyled>
                    )
                ) : (
                    <LoyaltyClubBenefitsStyled>
                        <LoyaltyClubBenefitStyled>
                            <SimpleCheckmarkIcon className="mr-2.5 h-4 w-4 text-red" />
                            <div>{t('Earn points starting with the first order')}</div>
                        </LoyaltyClubBenefitStyled>
                        <LoyaltyClubBenefitStyled>
                            <SimpleCheckmarkIcon className="mr-2.5 h-4 w-4 text-red" />
                            <div>{t('Gifts and rewards')}</div>
                        </LoyaltyClubBenefitStyled>
                        <LoyaltyClubBenefitStyled>
                            <SimpleCheckmarkIcon className="mr-2.5 h-4 w-4 text-red" />
                            <div>{t('More time to return goods')}</div>
                        </LoyaltyClubBenefitStyled>
                    </LoyaltyClubBenefitsStyled>
                )}
            </LoyaltyClubLeftColumnStyled>
            <LoyaltyClubRightColumnStyled>
                {isUserLoggedIn ? (
                    loyaltyClubInfo && (
                        <>
                            {disableControlsBecauseVoucher ? (
                                <div className="relative">
                                    <div style={{ position: 'absolute', width: '100%', height: '100%', zIndex: 1 }}>
                                        <Tooltip
                                            label={t('Points cannot be used for orders containing vouchers.')}
                                            placement="top-end"
                                        >
                                            <div style={{ width: '100%', height: '100%' }}></div>
                                        </Tooltip>
                                    </div>
                                    <LoyaltyClubUsePointsForm
                                        pointsApplied={pointsToBeApplied}
                                        maximumApplicableLoyaltyPoints={maximumApplicableLoyaltyPoints}
                                        applyLoyaltyPointsButtonDisabled
                                    />
                                </div>
                            ) : (
                                <LoyaltyClubUsePointsForm
                                    pointsApplied={pointsToBeApplied}
                                    onPointsToBeAppliedValueChange={(currentValue) => {
                                        setIsZeroPoints(currentValue <= 0);
                                        setPointsToBeApplied(currentValue);
                                    }}
                                    maximumApplicableLoyaltyPoints={maximumApplicableLoyaltyPoints}
                                    applyLoyaltyPointsButtonDisabled={isZeroPoints}
                                />
                            )}
                            <div>
                                <LoyaltyClubPointsInfoStyled>
                                    {disableControlsBecauseVoucher
                                        ? t(
                                              'Although you have {{count}} points in your account, they cannot be used for orders containing vouchers.',
                                              {
                                                  count: loyaltyClubInfo.loyaltyClubPoints,
                                              },
                                          )
                                        : `${t('You have {{count}} points in your account', {
                                              count: loyaltyClubInfo.loyaltyClubPoints,
                                          })} ${t('After applying, you will have {{ count }} points left', {
                                              postProcess: 'interval',
                                              count: loyaltyClubInfo.loyaltyClubPoints - pointsToBeApplied,
                                          })}`}
                                </LoyaltyClubPointsInfoStyled>
                            </div>
                        </>
                    )
                ) : (
                    <LoyaltyClubSigningButtonWrapperStyled>
                        <SignUpWrapperStyled>
                            <HighLinkStyled href={registrationUrl} isButton size="small">
                                {t('Sign up to the club and get benefits')}
                            </HighLinkStyled>
                            <LoyaltyClubBenefitsLinkStyled href={tescomaClubUrl} size="small" target="_blank">
                                {t('Still not sure if you should become a member?')}
                            </LoyaltyClubBenefitsLinkStyled>
                        </SignUpWrapperStyled>
                        <HighButtonStyled type="button" size="small" onClick={() => setIsUserPopupOpened(true)}>
                            {t('Sign in')}
                        </HighButtonStyled>
                    </LoyaltyClubSigningButtonWrapperStyled>
                )}
            </LoyaltyClubRightColumnStyled>
        </LoyaltyClubStyled>
    );
};

export default LoyaltyClubContent;
